import type { Json } from "../primitives";
import { dateBoundMs } from "./dates";
import {
  isCondition,
  operatorDefinition,
  type FieldDefinition,
  type Filter,
  type FilterNode,
} from "./model";

export interface FilterProblem {
  /** Path from the root, as child indexes. `[0, 2]` is the third child of the first. */
  path: number[];
  message: string;
}

function asFiniteNumber(value: Json | undefined): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Why a `between` whose start is past its end can never match, or null when it
 * can (#518).
 *
 * Dates are compared as the moments they name, in the store's zone, so "5 Oct to
 * 1 Oct" is backwards and "1 Oct to 1 Oct" is the one day it says; numbers as
 * numbers, not as text. A value that is not a date or a number is not this
 * problem: it matches nothing as it always has, and says so no differently.
 */
function backwardsMessage(
  field: FieldDefinition,
  low: Json | undefined,
  high: Json | undefined,
): string | null {
  switch (field.type) {
    case "number":
    case "money": {
      const from = asFiniteNumber(low);
      const to = asFiniteNumber(high);
      return from !== null && to !== null && from > to
        ? `${field.label}: the minimum is more than the maximum`
        : null;
    }
    case "date":
    case "datetime": {
      if (typeof low !== "string" || typeof high !== "string") return null;
      const from = dateBoundMs(low, "start");
      const to = dateBoundMs(high, "end");
      if (from === null || to === null || from <= to) return null;
      return field.type === "date"
        ? `${field.label}: the start date is after the end date`
        : `${field.label}: the start is after the end`;
    }
    default:
      return null;
  }
}

/**
 * Check a filter against the fields a module actually offers.
 *
 * Filters arrive from URLs and from saved views written months ago against a
 * field that has since been renamed. Both are untrusted in the same way: not
 * malicious, just stale. Reporting which clause is wrong lets the builder
 * highlight it, rather than the list silently returning nothing.
 */
export function validateFilter(
  filter: Filter,
  fields: FieldDefinition[],
): FilterProblem[] {
  const byName = new Map(fields.map((f) => [f.name, f]));
  const problems: FilterProblem[] = [];

  function walk(node: FilterNode, path: number[]): void {
    if (!isCondition(node)) {
      node.children.forEach((child, index) => {
        walk(child, [...path, index]);
      });
      return;
    }

    const field = byName.get(node.field);
    if (!field) {
      problems.push({ path, message: `Unknown field "${node.field}"` });
      return;
    }
    if (field.filterable === false) {
      problems.push({ path, message: `${field.label} cannot be filtered` });
      return;
    }

    const definition = operatorDefinition(field.type, node.operator);
    if (!definition) {
      problems.push({
        path,
        message: `"${node.operator}" does not apply to ${field.label}`,
      });
      return;
    }

    const { arity } = definition;
    const value = node.value;

    if (arity === "none") {
      if (value !== undefined) {
        problems.push({
          path,
          message: `${definition.label} takes no value`,
        });
      }
      return;
    }

    if (value === undefined || value === null || value === "") {
      problems.push({ path, message: `${field.label} needs a value` });
      return;
    }

    if (arity === "two") {
      // Both ends, not just two slots: the builder writes `["2026-09-30", ""]`
      // while the second date is still empty, which matches nothing — an empty
      // list under a chip that looks filled in (#492).
      if (
        !Array.isArray(value) ||
        value.length !== 2 ||
        value.some((end) => end === null || end === "")
      ) {
        problems.push({ path, message: `${field.label} needs two values` });
        return;
      }

      // Both ends set and the range runs backwards: it matches nothing, under a
      // chip that reads as if it were fine (#518).
      const [low, high] = value;
      const backwards = backwardsMessage(field, low, high);
      if (backwards) problems.push({ path, message: backwards });
      return;
    }

    if (arity === "many") {
      if (!Array.isArray(value) || value.length === 0) {
        problems.push({
          path,
          message: `${field.label} needs at least one value`,
        });
      }
      return;
    }

    if (Array.isArray(value)) {
      problems.push({ path, message: `${field.label} takes a single value` });
    }
  }

  walk(filter, []);
  return problems;
}
